import {
  Component,
  ErrorHandler,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { BackdoorEulaModalComponent } from '@app/modals/backdoor-eula-modal/backdoor-eula-modal.component';
import { BackdoorInfoModalComponent } from '@app/modals/backdoor-info-modal/backdoor-info-modal.component';
import { IBackdoorEula } from '@backend/models/types/backdoor-eula';
import { IBackdoorInfo } from '@backend/models/types/backdoor-info';
import { EDailySurveyMode } from '@backend/types/daily-survey-definition';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ModalController } from '@ionic/angular';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { add } from 'date-fns';
import {
  filter,
  interval,
  ReplaySubject,
  Subscription,
  switchMap,
  take
} from 'rxjs';
import { slk } from 'survey-core';
import { AuthenticationService } from './core/authentication/authentication.service';
import { BackdoorApiService } from './core/service/api/backdoor.api.service';
import { TenantApiService } from './core/service/api/tenant.api.service';
import { TodaysShiftApiService } from './core/service/api/todaysShift.api.service';
import { AppRefreshService } from './core/service/app-refresh.service';
import { EnvironmentService } from './core/service/environment.service';
import { LocalStorageService } from './core/service/local-storage.service';
import { PasscodeTimeoutService } from './core/service/passcode-timeout.service';
import { ThemeService } from './core/service/theme.service';
import { UserEntityService } from './core/service/user-entity.service';
import { WhatsNewService } from './core/service/whats-new.service';
import { FcmService } from './fcm/fcm.service';
import { MongoStoredObject } from './types/mongo-stored-object';
import { showModal } from './utils/modal';

const WHITELISTED_IDLE_PAGES = ['/login', '/pin', '/404'];

interface IDeviceInfo {
  version: string;
  platform: string;
  manufacturer: string;
  model: string;
  osVersion: string;
  operatingSystem: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  protected timeToIdle = 5;
  protected timeToIdleLeft = this.timeToIdle;
  protected whatsNewShown = false;
  protected isInstantFeedbackEnabled = false;
  protected lastPauseTime: Date;
  protected appIsFocused = false;
  protected deviceInfo = new ReplaySubject<IDeviceInfo>(1);
  private _fcmSubscription?: Subscription;

  public constructor(
    private readonly _modalCtrl: ModalController,
    private readonly _backdoorApiService: BackdoorApiService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _authenticationService: AuthenticationService,
    private readonly _userEntityService: UserEntityService,
    private readonly _router: Router,
    private readonly _passcodeTimeoutService: PasscodeTimeoutService,
    private readonly _whatsNewService: WhatsNewService,
    private readonly _themeService: ThemeService,
    private readonly _todaysShiftApiService: TodaysShiftApiService,
    private readonly _ngZone: NgZone,
    private readonly _tenantApiService: TenantApiService,
    private readonly _errorHandler: ErrorHandler,
    private readonly _fcmService: FcmService,
    appRefreshService: AppRefreshService
  ) {
    void appRefreshService;
    slk(
      'ZmI1NmE3MDItMzEzNy00NWJlLWIzODktOWNmMDdiZGNmMGJkJmRvbWFpbnM6dmFsY2hlbXkuY29tLHZhbGNoZW15LmFwcCx2YWxjaGVteS5kZXY7MT0yMDI2LTA0LTIzLDI9MjAyNi0wNC0yMyw0PTIwMjYtMDQtMjM='
    );

    this._passcodeTimeoutService.timeToIdle$.subscribe((timeToIdle) => {
      this.timeToIdle = timeToIdle;
    });
    this._passcodeTimeoutService.timeToIdleLeft$.subscribe((timeToIdleLeft) => {
      this.timeToIdleLeft = timeToIdleLeft;
    });

    this._passcodeTimeoutService.initPasscodeTimeout();

    App.addListener('pause', () => {
      this.appIsFocused = false;
      this.lastPauseTime = new Date();
    });

    // CHANGED: On resume, now passes dailySurveyPNTime so the end-of-shift
    // survey can also trigger when resuming the app (if within the window).
    // Previously this passed `undefined`, meaning the survey never triggered on resume.
    App.addListener('resume', () => {
      this.appIsFocused = true;

      if (
        new Date().valueOf() > add(this.lastPauseTime, { hours: 1 }).valueOf()
      ) {
        if (this._authenticationService.user) {
          this._tenantApiService
            .getTenantById({
              path: { id: this._authenticationService.user.tenant }
            })
            .pipe(take(1))
            .subscribe({
              next: async (tenant) => {
                if (tenant.features.askTodaysShiftTime) {
                  await this._todaysShiftApiService.askTodaysShift(
                    tenant.features.survey &&
                      tenant.settings.dailySurvey?.dailySurveyMode ===
                        EDailySurveyMode.PUSH_NOTIFICATION
                      ? tenant.settings.dailySurvey?.dailySurveyPNTime
                      : undefined,
                    tenant.timezone
                  );
                }
              },
              error: (error) => {
                this._errorHandler.handleError(error);
              }
            });

          this._whatsNewService.checkWhatsNew();
        }
      }

      this.deviceInfo
        .pipe(
          take(1),
          switchMap((deviceInfo) =>
            this._backdoorApiService.getBackdoorState({ query: deviceInfo })
          )
        )
        .subscribe({
          next: this._processBackdoorStatus.bind(this),
          error: (error) => {
            this._errorHandler.handleError(error);
          }
        });
    });

    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this._ngZone.run(() => {
        try {
          const domain = 'valchemy.app';
          let queryParams: object = {};

          const pathArray = event.url.split(domain);

          let appPath = pathArray.pop();

          if (appPath) {
            if (appPath.includes('?')) {
              const pathArray = appPath.split('?');

              if (pathArray.length > 1) {
                appPath = pathArray.shift();

                const params = new URLSearchParams(pathArray.pop());
                const entries = params.entries();
                queryParams = Object.fromEntries(entries);
              }
            }

            if (appPath?.includes('#')) {
              const pathArray = appPath.split('#');

              if (pathArray.length > 1) {
                appPath = pathArray.pop();
              }
            }

            this._router.navigate([appPath], { queryParams });
          }
        } catch (e) {
          this._errorHandler.handleError(e);
        }
      });
    });
  }

  private _processBackdoorStatus(data: {
    currentInfo: MongoStoredObject<IBackdoorInfo> | null;
    currentEula: MongoStoredObject<IBackdoorEula> | null;
    isUserDeleted: boolean;
  }) {
    this._showBackdoorEula(data.currentEula);
    this._showBackdoorInfo(data.currentInfo);
    if (data.isUserDeleted) {
      this._authenticationService.logout();
    }
  }

  protected get showIdleTimer() {
    const multiUserMode = !!this._localStorageService.getItem('multiUserMode');

    return multiUserMode && !WHITELISTED_IDLE_PAGES.includes(this._router.url);
  }

  protected get timeToIdleProgress() {
    return this.timeToIdleLeft / this.timeToIdle;
  }

  public async ngOnInit() {
    this._fcmSubscription = this._fcmService.initFCM().subscribe({
      error: (error) => {
        this._errorHandler.handleError(error);
      }
    });

    this._themeService.init();

    Device.getInfo()
      .then(async (deviceInfo) => {
        this.deviceInfo.next({
          version: EnvironmentService.version,
          platform: deviceInfo.platform,
          manufacturer: deviceInfo.manufacturer,
          model: deviceInfo.model,
          osVersion: deviceInfo.osVersion,
          operatingSystem: deviceInfo.operatingSystem
        });

        if (Capacitor.getPlatform() !== 'web') {
          await this._initStatusBar();

          if (Capacitor.getPlatform() === 'ios') {
            await Keyboard.setScroll({ isDisabled: true });
          }
        } else {
          await defineCustomElements(window);
        }

        this.appIsFocused = true;
        interval(60 * 1000) // 1 minute
          .pipe(
            filter(() => this.appIsFocused),
            switchMap(() => this.deviceInfo.pipe(take(1))),
            switchMap((deviceInfo) =>
              this._backdoorApiService.getBackdoorState({
                query: deviceInfo
              })
            )
          )
          .subscribe({
            next: this._processBackdoorStatus.bind(this),
            error: (error) => {
              this._errorHandler.handleError(error);
            }
          });

        this._userEntityService.user$.subscribe(async (user) => {
          if (user) {
            this._tenantApiService
              .getTenantById({ path: { id: user.tenant } })
              // TODO: wouldn't be better just to subscribe to a tenant and not load it from API
              .pipe(take(1))
              .subscribe({
                next: async (tenant) => {
                  if (tenant.features.askTodaysShiftTime) {
                    await this._todaysShiftApiService.askTodaysShift(
                      tenant.features.survey &&
                        tenant.settings.dailySurvey?.dailySurveyMode ===
                          EDailySurveyMode.PUSH_NOTIFICATION
                        ? tenant.settings.dailySurvey?.dailySurveyPNTime
                        : undefined,
                      tenant.timezone
                    );
                  }

                  this.isInstantFeedbackEnabled =
                    tenant.features.instantFeedback;
                },
                error: (error) => {
                  this._errorHandler.handleError(error);
                }
              });

            this._whatsNewService.checkWhatsNew();

            setTimeout(() => {
              this.deviceInfo
                .pipe(
                  take(1),
                  switchMap((deviceInfo) =>
                    this._backdoorApiService.getBackdoorState({
                      query: deviceInfo
                    })
                  )
                )
                .subscribe({
                  next: this._processBackdoorStatus.bind(this),
                  error: (error) => {
                    this._errorHandler.handleError(error);
                  }
                });
            }, 1000);
          }
        });
      })
      .catch((error) => {
        this._errorHandler.handleError(error);
      });
  }

  public ngOnDestroy(): void {
    this._fcmSubscription?.unsubscribe();
  }

  private async _showBackdoorInfo(
    backdoorInfo?: MongoStoredObject<IBackdoorInfo> | null
  ) {
    if (backdoorInfo) {
      showModal(
        {
          component: BackdoorInfoModalComponent,
          props: { backdoorInfo },
          modalName: 'BackdoorInfoModalComponent'
        },
        this._modalCtrl,
        {
          useTopModalAsParent: true,
          canDismiss: backdoorInfo.canDismiss,
          backdropDismiss: backdoorInfo.canDismiss
        }
      );
    }
  }

  private async _showBackdoorEula(
    backdoorEula?: MongoStoredObject<IBackdoorEula> | null
  ) {
    if (backdoorEula) {
      showModal(
        {
          component: BackdoorEulaModalComponent,
          props: { backdoorEula },
          modalName: 'BackdoorEulaModalComponent'
        },
        this._modalCtrl,
        {
          useTopModalAsParent: true,
          canDismiss: async (_, role) => role === 'confirm',
          backdropDismiss: false
        }
      );
    }
  }

  private async _initStatusBar() {
    try {
      await StatusBar.setOverlaysWebView({ overlay: true });
      await StatusBar.setStyle({
        style:
          this._themeService.activeTheme === 'dark' ? Style.Dark : Style.Light
      });

      const { insets } = await SafeArea.getSafeAreaInsets();

      if (Capacitor.getPlatform() === 'android') {
        document.documentElement.style.setProperty(
          '--ion-safe-area-top',
          insets.top + 'px'
        );
        document.documentElement.style.setProperty(
          '--ion-safe-area-bottom',
          insets.bottom + 'px'
        );
        document.documentElement.style.setProperty(
          '--ion-safe-area-left',
          insets.left + 'px'
        );
        document.documentElement.style.setProperty(
          '--ion-safe-area-right',
          insets.right + 'px'
        );
      }
    } catch (e) {
      this._errorHandler.handleError(e);
    }
  }
}
